import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

/**
 * 가상 조이스틱.
 *
 * 터치 기기에는 WASD가 없다. 방향 버튼 네 개(D-pad)를 두는 것보다 이 편이
 * 낫다 — 대각선으로 걷기가 자연스럽고, 손가락이 버튼 사이 틈에 빠지지
 * 않는다. 사무실 통로가 한 칸짜리라 대각선 이동이 실제로 자주 쓰인다.
 *
 * `setPointerCapture`로 손가락을 붙잡는다. 안 붙잡으면 스틱 바깥으로
 * 나가는 순간 이벤트가 끊겨 방향이 그 자리에 굳는다 — 아바타가 벽으로
 * 계속 걸어간다.
 */

/** 바깥 원의 반지름(px). 손가락을 덮고도 남을 크기. */
const BASE_RADIUS = 52;
/** 손잡이가 중심에서 벗어날 수 있는 최대 거리. */
const KNOB_TRAVEL = 34;

export function Joystick({ onChange }: { onChange: (dx: number, dy: number) => void }) {
  const baseRef = useRef<HTMLDivElement>(null);
  const pointerId = useRef<number | null>(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });

  function update(event: ReactPointerEvent<HTMLDivElement>) {
    const base = baseRef.current;
    if (base === null) return;

    const rect = base.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    let dx = event.clientX - centerX;
    let dy = event.clientY - centerY;

    // 손잡이는 원 안에만 머문다. 벗어나면 방향만 남기고 거리를 자른다.
    const distance = Math.hypot(dx, dy);
    if (distance > KNOB_TRAVEL) {
      dx = (dx / distance) * KNOB_TRAVEL;
      dy = (dy / distance) * KNOB_TRAVEL;
    }

    setKnob({ x: dx, y: dy });
    onChange(dx / KNOB_TRAVEL, dy / KNOB_TRAVEL);
  }

  function release() {
    pointerId.current = null;
    setKnob({ x: 0, y: 0 });
    onChange(0, 0);
  }

  return (
    <div
      ref={baseRef}
      // 이 위의 드래그는 전부 우리 것이다. 안 막으면 페이지가 따라 움직이고
      // 브라우저가 새로고침 제스처로 오해한다.
      className="no-touch-scroll absolute bottom-4 left-4 rounded-full border border-neutral-600/60 bg-black/35"
      style={{ width: BASE_RADIUS * 2, height: BASE_RADIUS * 2 }}
      aria-label="이동 조이스틱"
      onPointerDown={(event) => {
        // 두 번째 손가락은 무시한다. 상호작용 버튼을 같이 누를 수 있어야 한다.
        if (pointerId.current !== null) return;
        pointerId.current = event.pointerId;
        event.currentTarget.setPointerCapture(event.pointerId);
        update(event);
      }}
      onPointerMove={(event) => {
        if (pointerId.current !== event.pointerId) return;
        update(event);
      }}
      onPointerUp={(event) => {
        if (pointerId.current === event.pointerId) release();
      }}
      // 취소는 전화가 오거나 브라우저가 제스처로 가로챌 때 온다.
      // 여기서 놓지 않으면 방향이 굳는다.
      onPointerCancel={(event) => {
        if (pointerId.current === event.pointerId) release();
      }}
    >
      <div
        className="pointer-events-none absolute rounded-full bg-neutral-200/70"
        style={{
          width: 36,
          height: 36,
          left: BASE_RADIUS - 18 + knob.x,
          top: BASE_RADIUS - 18 + knob.y,
        }}
      />
    </div>
  );
}
